import { useMemo } from 'react';
import { useAppState } from '../contexts/AppStateContext';
import { DEMO_DATE } from '../data/demo';
import { FamilyEvent, PartOfDay, TaskTemplate } from '../types/family';
import { currentPart, eventPart, isOpportunityTask, isWeeklyTask, partOfDayOf, partOrder } from '../utils/dayParts';
import { equalShares, isDoneStatus } from '../utils/progress';
import { opportunityOn } from '../utils/rewards';
import { isDuty } from '../utils/duties';
import { formatDate } from '../utils/time';
import { isDueOn, TodayItem, todayItemsFor } from '../utils/today';

export interface StudyRow {
  key: string;
  label: string;
  sub: string;
  done: boolean;
  toggle: () => void;
}

export interface DayPartData {
  part: PartOfDay;
  tasks: TodayItem[];
  study: StudyRow[];
  events: FamilyEvent[];
  total: number;
  done: number;
}

/** Cvenkotočke, ki jih izvedba prinese temu članu (pri deljenih opravilih njegov delež). */
export function pointsFor(t: TaskTemplate, memberId: string): number {
  if (!t.baseValue || t.valueType !== 'osnovno') return 0;
  if (t.assigneeIds.length > 1) return equalShares(t.baseValue, t.assigneeIds)[memberId] ?? 0;
  return t.baseValue;
}

export function useTodayPlan() {
  const { family, viewer, familyOcc, db, toggleBlock, toggleSchoolItem } = useAppState();
  const now = currentPart();

  return useMemo(() => {
    const items = todayItemsFor(family, viewer.id, familyOcc, db.exceptions);
    const daily = items.filter((i) => !isWeeklyTask(i.template) && !isOpportunityTask(i.template));
    const weekly = items.filter((i) => isWeeklyTask(i.template) && !isDuty(i.template));
    const duties = family.modules.hisnaOpravila ? family.templates.filter((t) => isDuty(t) && t.assigneeIds.includes(viewer.id)) : [];
    const roomDay = opportunityOn(family.rules, 'roomDay') ? items.filter((i) => isOpportunityTask(i.template)) : [];

    const study: (StudyRow & { part: PartOfDay })[] = db.schoolItems
      .filter((s) => s.familyId === family.id && s.memberId === viewer.id)
      .flatMap((s) => [
        ...s.blocks
          .filter((b) => b.date === DEMO_DATE)
          .map((b) => ({
            key: b.id,
            label: `Učenje: ${s.title}`,
            sub: `${b.minutes} min · ${s.kind === 'test' ? 'test' : 'rok'} ${formatDate(s.date)}`,
            done: b.done,
            toggle: () => toggleBlock(s.id, b.id),
            part: 'popoldne' as PartOfDay,
          })),
        ...(s.blocks.length === 0 && s.date === DEMO_DATE
          ? [{ key: s.id, label: s.title, sub: 'rok danes', done: s.done, toggle: () => toggleSchoolItem(s.id), part: 'popoldne' as PartOfDay }]
          : []),
      ]);

    const events = family.events
      .filter((e) => e.date === DEMO_DATE && (e.participantIds.length === 0 || e.participantIds.includes(viewer.id) || e.driverId === viewer.id))
      .sort((a, b) => a.start.localeCompare(b.start));

    const counts = (i: TodayItem) => !i.excused && i.relation !== 'predano';
    const parts: DayPartData[] = partOrder.map((part) => {
      const tasks = daily.filter((i) => partOfDayOf(i.template) === part);
      const partStudy = study.filter((s) => s.part === part);
      const actionable = tasks.filter(counts);
      return {
        part,
        tasks,
        study: partStudy,
        events: events.filter((e) => eventPart(e) === part),
        total: actionable.length + partStudy.length,
        done: actionable.filter((i) => isDoneStatus(i.occ.status)).length + partStudy.filter((s) => s.done).length,
      };
    });

    const shared =
      family.modules.zivali && opportunityOn(family.rules, 'sharedCare')
        ? family.templates.filter((t) => t.assigneeIds.length === 0 && family.modules[t.module] && isDueOn(t, DEMO_DATE))
        : [];
    const offers = opportunityOn(family.rules, 'offers')
      ? family.templates.filter((t) => {
          const o = familyOcc[t.id];
          return o?.offer && !o.takenBy && o.offer.byId !== viewer.id && (o.offer.toId === viewer.id || o.offer.toId === 'vsi');
        })
      : [];

    const total = parts.reduce((s, p) => s + p.total, 0);
    const done = parts.reduce((s, p) => s + p.done, 0);
    const pointsToday = daily
      .filter((i) => counts(i) && isDoneStatus(i.occ.status))
      .reduce((s, i) => s + pointsFor(i.template, viewer.id), 0);

    return { parts, weekly, duties, roomDay, shared, offers, now, total, done, allDone: total > 0 && done === total, pointsToday };
  }, [family, viewer.id, familyOcc, db.exceptions, db.schoolItems, toggleBlock, toggleSchoolItem, now]);
}
